<!--
  - SPDX-FileCopyrightText: 2020 Nextcloud GmbH and Nextcloud contributors
  - SPDX-License-Identifier: AGPL-3.0-or-later
-->

<template>
	<Question
		v-bind="questionProps"
		:titlePlaceholder="answerType.titlePlaceholder"
		:warningInvalid="answerType.warningInvalid"
		:contentValid="contentValid"
		:shiftDragHandle="shiftDragHandle"
		:errorMessage="errorMessage"
		v-on="commonListeners">
		<template #actions>
			<NcActionCheckbox
				:modelValue="extraSettings?.shuffleOptions"
				@update:modelValue="onShuffleOptionsChange">
				{{ t('forms', 'Shuffle options') }}
			</NcActionCheckbox>
			<NcActionButton closeAfterClick @click="isOptionDialogShown = true">
				<template #icon>
					<NcIconSvgWrapper :svg="IconContentPaste" />
				</template>
				{{ t('forms', 'Add multiple options') }}
			</NcActionButton>
		</template>
		<div
			v-if="readOnly"
			class="question__content"
			role="group"
			:aria-labelledby="titleId"
			:aria-describedby="description ? descriptionId : undefined">
			<NcSelect
				:modelValue="selectedOption"
				:name="name || undefined"
				:placeholder="selectOptionPlaceholder"
				:multiple="isMultiple"
				:required="isRequired"
				:options="choices"
				:searchable="false"
				label="text"
				:aria-label-combobox="selectOptionPlaceholder"
				:aria-errormessage="hasError ? errorId : undefined"
				:aria-invalid="hasError ? 'true' : undefined"
				@invalid.prevent="validate"
				@update:modelValue="onInput" />
		</div>
		<template v-else>
			<div v-if="isLoading">
				<NcLoadingIcon :size="64" />
			</div>
			<Draggable
				v-else
				v-model="choices"
				class="question__content"
				:animation="300"
				direction="vertical"
				handle=".option__drag-handle"
				invertSwap
				target=".sort-target"
				@update="dirtyOptionsType = 'choice'"
				@start="onDragStart"
				@end="onDragEnd">
				<TransitionGroup
					tag="ul"
					:name="isDragging ? undefined : 'options-list-transition'"
					class="sort-target">
					<AnswerInput
						v-for="(answer, index) in choices"
						:key="answer.local ? 'option-local' : answer.id"
						ref="input"
						:answer="answer"
						:formId="formId"
						isDropdown
						:index="index"
						:isUnique="!isMultiple"
						:maxIndex="options.length - 1"
						:maxOptionLength="maxStringLengths.optionText"
						optionType="choice"
						@createAnswer="onCreateAnswer"
						@update:answer="updateAnswer"
						@delete="deleteOption"
						@focusNext="focusNextInput"
						@moveUp="onOptionMoveUp(index, OptionType.Choice)"
						@moveDown="onOptionMoveDown(index, OptionType.Choice)"
						@tabbedOut="checkValidOption" />
				</TransitionGroup>
			</Draggable>
		</template>

		<!-- Add multiple options modal -->
		<OptionInputDialog
			v-model:open="isOptionDialogShown"
			@multipleAnswers="handleMultipleOptions" />
		<template #insert>
			<slot name="insert" />
		</template>
	</Question>
</template>

<script lang="ts">
import type { FormsOption } from '../../types/Entities.d.ts'

import IconContentPaste from '@material-symbols/svg-400/outlined/content_paste.svg?raw'
import { t } from '@nextcloud/l10n'
import { computed, defineComponent, nextTick, ref } from 'vue'
import { VueDraggable as Draggable } from 'vue-draggable-plus'
import NcActionButton from '@nextcloud/vue/components/NcActionButton'
import NcActionCheckbox from '@nextcloud/vue/components/NcActionCheckbox'
import NcIconSvgWrapper from '@nextcloud/vue/components/NcIconSvgWrapper'
import NcLoadingIcon from '@nextcloud/vue/components/NcLoadingIcon'
import NcSelect from '@nextcloud/vue/components/NcSelect'
import OptionInputDialog from '../OptionInputDialog.vue'
import AnswerInput from './AnswerInput.vue'
import Question from './Question.vue'
import {
	QUESTION_EMITS,
	QUESTION_PROPS,
	useQuestion,
} from '../../composables/useQuestion.ts'
import {
	QUESTION_MULTIPLE_EMITS,
	useQuestionMultiple,
} from '../../composables/useQuestionMultiple.ts'
import { OptionType } from '../../models/Constants.ts'

export default defineComponent({
	name: 'QuestionDropdown',

	components: {
		AnswerInput,
		Draggable,
		NcIconSvgWrapper,
		NcActionButton,
		NcActionCheckbox,
		NcLoadingIcon,
		NcSelect,
		OptionInputDialog,
		Question,
	},

	props: QUESTION_PROPS,
	emits: [...QUESTION_EMITS, ...QUESTION_MULTIPLE_EMITS, 'update:values'],

	setup(props, { emit }) {
		const question = useQuestion(props, { emit })
		const values = computed<Array<number | string>>(() => {
			return Array.isArray(props.values) ? props.values : []
		})
		const input = ref<
			Array<{
				focus?: () => void
				$props?: { optionType?: string; index?: number }
			} | null>
		>([])
		const isLoading = ref(false)
		const questionMultiple = useQuestionMultiple(props, {
			emit,
			input,
			isLoading,
		})
		const isDragging = ref(false)
		const isOptionDialogShown = ref(false)

		const selectOptionPlaceholder = computed(() => {
			if (props.readOnly) {
				return props.answerType?.submitPlaceholder
			}
			return props.answerType?.createPlaceholder
		})

		const isMultiple = computed(() => {
			// This can be extended if we want to include support for <select multiple>
			return false
		})

		const shiftDragHandle = computed(() => {
			return (
				!props.readOnly
				&& props.options.length !== 0
				&& !questionMultiple.isLastEmpty.value
			)
		})

		const selectedOption = computed<FormsOption | FormsOption[] | null>(() => {
			if (values.value.length === 0) {
				return null
			}

			const selected = values.value
				.map((id) =>
					props.options?.find(
						(option) => option.id === parseInt(String(id), 10),
					),
				)
				.filter((option): option is FormsOption => option !== undefined)

			return isMultiple.value ? selected : (selected[0] ?? null)
		})

		const choices = computed<FormsOption[]>({
			get() {
				return questionMultiple.sortOptionsOfType(
					props.options,
					OptionType.Choice,
				)
			},
			set(value: FormsOption[]) {
				questionMultiple.updateOptionsOrder(value, OptionType.Choice)
			},
		})

		const validate = async (): Promise<boolean> => {
			if (props.isRequired && questionMultiple.areNoneChecked.value) {
				question.errorMessage.value = t(
					'forms',
					'You must answer this question',
				)
				return false
			}

			question.errorMessage.value = null
			return true
		}

		const onDragStart = (): void => {
			isDragging.value = true
		}

		const onDragEnd = (): void => {
			nextTick(() => {
				isDragging.value = false
			})
		}

		const onInput = (option: FormsOption | FormsOption[] | null): void => {
			if (Array.isArray(option)) {
				emit('update:values', [...new Set(option.map((opt) => opt.id))])
				return
			}

			// Simple select
			emit('update:values', option ? [option.id] : [])
		}

		return {
			...question,
			...questionMultiple,
			input,
			choices,
			isDragging,
			isLoading,
			isMultiple,
			isOptionDialogShown,
			IconContentPaste,
			onDragEnd,
			onDragStart,
			onInput,
			OptionType,
			selectedOption,
			selectOptionPlaceholder,
			shiftDragHandle,
			t,
			validate,
			values,
		}
	},
})
</script>

<style lang="scss" scoped>
.question__content {
	display: flex;
	flex-direction: column;
	gap: var(--default-grid-baseline);
}

.question__item {
	position: relative;
	display: inline-flex;
	min-height: var(--default-clickable-area);

	.question__input {
		width: calc(100% - var(--default-clickable-area));
		position: relative;
		inset-inline-start: -12px;
		margin-inline-end: 32px !important;
	}
}

.options-list-transition-move,
.options-list-transition-enter-active,
.options-list-transition-leave-active {
	transition: all var(--animation-slow) ease;
}

.options-list-transition-enter-from,
.options-list-transition-leave-to {
	opacity: 0;
	transform: translateX(var(--default-clickable-area));
}

/* ensure leaving items are taken out of layout flow so that moving
   animations can be calculated correctly. */
.options-list-transition-leave-active {
	position: absolute;
}
</style>
